import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  fetchWallet,
  setCoinPin,
  fetchCoinTransactions,
} from "@/lib/api";
import CreatorGiveaway from "@/components/CreatorGiveaway";
import {
  Coins,
  Lock,
  History,
  ChevronDown,
  ShieldCheck,
  Gift,
} from "lucide-react";
import { toast } from "sonner";

function TypeBadge({ t, dir }) {
  if (t === "purchase") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500/15 text-orange-300 text-xs">
        Purchase
      </span>
    );
  }

  if (t === "sync" || t === "reward") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs">
        {t === "sync" ? "Server Sync" : "Reward"}
      </span>
    );
  }

  if (t === "admin") {
    return (
      <span className="px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-300 text-xs">
        Admin
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${
        dir === "sent"
          ? "bg-orange-500/15 text-orange-300"
          : "bg-emerald-500/15 text-emerald-300"
      }`}
    >
      {dir === "sent" ? "Sent" : "Received"}
    </span>
  );
}

export default function Wallet() {
  const { user } = useAuth();

  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [pin, setPin] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [giveawayOpen, setGiveawayOpen] = useState(false);
  const [savingPin, setSavingPin] = useState(false);

  const isCreator =
    user?.is_creator ||
    user?.role === "creator";

  const isAdmin = user?.role === "admin";

  const walletPermissions = wallet?.wallet_permissions || {};

  const canUsePin =
    isCreator ||
    isAdmin ||
    walletPermissions.pin_set === true;

  const canUseGiveaway =
    isCreator ||
    isAdmin ||
    walletPermissions.giveaway === true;

  const load = useCallback(() => {
    fetchWallet().then(setWallet).catch(() => {});
    fetchCoinTransactions().then(setTxs).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const savePin = async () => {
    if (!pin) {
      toast.error("Enter your PIN");
      return;
    }

    setSavingPin(true);

    try {
      await setCoinPin(pin);
      setPin("");
      setPinOpen(false);
      toast.success("Transfer PIN saved");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to save PIN"
      );
    } finally {
      setSavingPin(false);
    }
  };

  if (!user) {
    return (
      <div
        className="min-h-screen pt-32 pb-24 px-4 text-center text-white/60"
        data-testid="wallet-login"
      >
        <p>
          Please sign in to view your wallet.{" "}
          <Link to="/store" className="text-[#FFBF00]">
            Go to store
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pt-28 sm:pt-32 pb-16 sm:pb-24 px-3 sm:px-6"
      data-testid="page-wallet"
    >
      <div className="w-full max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-[#FFBF00]/15 flex items-center justify-center">
              <Coins
                size={24}
                className="text-[#FFBF00]"
              />
            </div>

            <div>
              <h1 className="font-display text-2xl sm:text-3xl text-white">
                DC Coin Wallet
              </h1>

              <p className="text-xs sm:text-sm text-white/40 mt-1">
                Manage your LostDC Coins
              </p>
            </div>
          </div>
        </div>

        {/* BALANCE + PIN DROPDOWN */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">

          {/* BALANCE CARD (DARK GLASS STYLE WITH WHITE TEXT) */}
          <div
            className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-white/10"
            data-testid="wallet-balance-card"
          >
            <div className="text-white/60 text-xs uppercase tracking-widest font-bold">
              LostDC ID
            </div>

            <div className="font-display text-lg sm:text-xl text-white mt-1 break-all">
              {wallet?.lostdc_id || "—"}
            </div>

            <div className="mt-5 text-white/60 text-xs uppercase tracking-widest font-bold">
              Balance
            </div>

            <div
              className="font-display text-4xl sm:text-5xl text-white mt-1 flex items-baseline gap-2"
              data-testid="wallet-balance"
            >
              <span>{wallet?.dc_coin ?? 0}</span>
              <span className="text-lg sm:text-xl text-[#FFBF00]">
                DC
              </span>
            </div>

            <div className="text-white/40 text-xs mt-2">
              5 DC = ₹1
            </div>
          </div>

          {/* TRANSFER PIN - PERMISSION CONTROLLED */}
          {canUsePin && (
            <div className="lg:col-span-2 glass rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 flex flex-col justify-center">
              <button
                type="button"
                onClick={() => setPinOpen((v) => !v)}
                className="w-full p-5 sm:p-6 flex items-center justify-between text-left hover:bg-white/[0.03] transition"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FFBF00]/10 flex items-center justify-center">
                    <Lock
                      size={18}
                      className="text-[#FFBF00]"
                    />
                  </div>

                  <div>
                    <h3 className="font-display text-base sm:text-lg text-white">
                      Transfer PIN
                    </h3>

                    <p className="text-xs text-white/40 mt-1">
                      {wallet?.has_pin
                        ? "Your transfer PIN is set"
                        : "Set a PIN for creator transfers"}
                    </p>
                  </div>

                  {wallet?.has_pin && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-xs text-emerald-400">
                      <ShieldCheck size={14} />
                      Set
                    </span>
                  )}
                </div>

                <ChevronDown
                  size={20}
                  className={`text-white/50 transition-transform duration-200 ${
                    pinOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {pinOpen && (
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 border-t border-white/10 pt-5">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="password"
                      inputMode="numeric"
                      maxLength={8}
                      value={pin}
                      onChange={(e) =>
                        setPin(
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      placeholder="Enter 4–8 digit PIN"
                      data-testid="wallet-pin-input"
                      className="w-full flex-1 px-4 py-3 rounded-xl bg-black/50 border border-white/10 text-white focus:border-[#FFBF00]/50 focus:outline-none"
                    />

                    <button
                      type="button"
                      onClick={savePin}
                      disabled={savingPin}
                      data-testid="wallet-pin-save"
                      className="w-full sm:w-auto px-6 py-3 rounded-xl bg-[#FFBF00] text-black font-bold blocky-shadow-black disabled:opacity-50"
                    >
                      {savingPin ? "Saving..." : "Save PIN"}
                    </button>
                  </div>

                  <p className="text-xs text-white/40 mt-3">
                    Keep your PIN private. It is required for creator DC Coin transfers.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* GIVEAWAY - PERMISSION CONTROLLED */}
        {canUseGiveaway && (
          <div className="mb-6 glass rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10">
            <button
              type="button"
              onClick={() => setGiveawayOpen((v) => !v)}
              className="w-full p-5 sm:p-6 flex items-center justify-between text-left hover:bg-white/[0.03] transition"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FFBF00]/10 flex items-center justify-center">
                  <Gift
                    size={18}
                    className="text-[#FFBF00]"
                  />
                </div>

                <div>
                  <h3 className="font-display text-base sm:text-lg text-white">
                    Send Giveaway Coins
                  </h3>

                  <p className="text-xs text-white/40 mt-1">
                    Manage and distribute coins to your community
                  </p>
                </div>
              </div>

              <ChevronDown
                size={20}
                className={`text-white/50 transition-transform duration-200 ${
                  giveawayOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {giveawayOpen && (
              <div className="p-4 sm:p-6 border-t border-white/10">
                <CreatorGiveaway />
              </div>
            )}
          </div>
        )}

        {/* TRANSACTION HISTORY */}
        <div
          className="glass rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-white/10"
          data-testid="wallet-history"
        >
          <div className="flex items-center justify-between gap-3 mb-5">
            <div>
              <h3 className="font-display text-lg sm:text-xl text-white flex items-center gap-2">
                <History
                  size={18}
                  className="text-[#FFBF00]"
                />
                Transaction History
              </h3>

              <p className="text-xs text-white/40 mt-1">
                Your recent DC Coin activity
              </p>
            </div>
          </div>

          {txs.length === 0 ? (
            <div className="py-12 text-center text-white/40 text-sm">
              No transactions yet.
            </div>
          ) : (
            <>
              {/* MOBILE */}
              <div className="space-y-3 md:hidden">
                {txs.map((t) => {
                  const other =
                    t.direction === "sent"
                      ? t.receiver_lostdc
                      : t.sender_lostdc;

                  return (
                    <div
                      key={t.id}
                      className="rounded-xl border border-white/10 bg-black/20 p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <TypeBadge
                          t={t.type}
                          dir={t.direction}
                        />

                        <span className="font-semibold text-white">
                          {t.amount} DC
                        </span>
                      </div>

                      <div className="mt-3 text-sm text-white/70 break-words">
                        {t.type === "purchase" ||
                        t.type === "sync" ||
                        t.type === "reward"
                          ? t.note
                          : other}
                      </div>

                      <div className="mt-2 text-xs text-white/35">
                        {new Date(
                          t.created_at
                        ).toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* DESKTOP */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/40 text-xs uppercase tracking-widest border-b border-white/5">
                      <th className="text-left py-3">Type</th>
                      <th className="text-left py-3">Details</th>
                      <th className="text-right py-3">
                        Amount
                      </th>
                      <th className="text-right py-3">
                        Date
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {txs.map((t) => {
                      const other =
                        t.direction === "sent"
                          ? t.receiver_lostdc
                          : t.sender_lostdc;

                      return (
                        <tr
                          key={t.id}
                          className="border-t border-white/5"
                          data-testid={`wallet-tx-${t.id}`}
                        >
                          <td className="py-4">
                            <TypeBadge
                              t={t.type}
                              dir={t.direction}
                            />
                          </td>

                          <td className="py-4 text-white/70">
                            {t.type === "purchase" ||
                            t.type === "sync" ||
                            t.type === "reward"
                              ? t.note
                              : other}
                          </td>

                          <td className="py-4 text-right font-semibold text-white">
                            {t.amount} DC
                          </td>

                          <td className="py-4 text-right text-white/40">
                            {new Date(
                              t.created_at
                            ).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}